import { ChangeEvent, useMemo, useRef, useState } from "react";
import { ImagePlus, Loader2, Sparkles, Star, X } from "lucide-react";
import { uploadProductImage } from "@/utils/cloudinary";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export interface ProductImageAsset {
  url: string;
  publicId?: string | null;
  alt?: string;
  isDefault?: boolean;
}

interface ProductImageUploaderProps {
  images: ProductImageAsset[];
  onChange: (images: ProductImageAsset[]) => void;
  maxImages?: number;
  onFilesUploaded?: (files: File[]) => void;
  analyzing?: boolean;
}

export function ProductImageUploader({
  images,
  onChange,
  maxImages = 8,
  onFilesUploaded,
  analyzing = false,
}: ProductImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const remainingSlots = Math.max(0, maxImages - images.length);
  const busy = isUploading;

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const filesToUpload = files.slice(0, remainingSlots);
    if (filesToUpload.length === 0) {
      toast.info(`You can upload up to ${maxImages} images per product.`);
      event.target.value = "";
      return;
    }

    onFilesUploaded?.(filesToUpload);
    setIsUploading(true);

    try {
      const uploadedAssets = await Promise.all(
        filesToUpload.map(async (file) => {
          const { url, publicId } = await uploadProductImage(file);
          return {
            url,
            publicId,
            alt: file.name,
          } satisfies ProductImageAsset;
        }),
      );

      onChange(ensureDefaultFlag([...images, ...uploadedAssets]));
      toast.success(
        `${uploadedAssets.length} image${uploadedAssets.length > 1 ? "s" : ""} uploaded`,
      );
    } catch (error) {
      console.error("Cloudinary upload error:", error);
      toast.error("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemove = (index: number) => {
    onChange(ensureDefaultFlag(images.filter((_, idx) => idx !== index)));
  };

  const handleSetDefault = (index: number) => {
    if (index === 0) return;
    const updated = [...images];
    const [selected] = updated.splice(index, 1);
    updated.unshift({ ...selected, isDefault: true });
    onChange(ensureDefaultFlag(updated));
  };

  const previews = useMemo(() => images, [images]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 sm:gap-3 lg:grid-cols-6 xl:grid-cols-8">
        {previews.map((image, index) => (
          <div
            key={image.publicId || image.url}
            className="relative aspect-square overflow-hidden rounded-2xl border border-[#EBEBEB] bg-[#faf9f7]"
          >
            <img
              src={image.url}
              alt={image.alt || `Product image ${index + 1}`}
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="absolute right-1.5 top-1.5 z-10 rounded-full bg-white/95 p-1.5 text-primary shadow-sm"
              aria-label="Remove image"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => handleSetDefault(index)}
              className={cn(
                "absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold",
                index === 0 ? "bg-primary text-primary-foreground" : "bg-card/95 text-primary",
              )}
            >
              <Star className={cn("h-3 w-3", index === 0 && "fill-current")} />
              {index === 0 ? "Cover" : "Set"}
            </button>
          </div>
        ))}

        {images.length < maxImages ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="flex aspect-square flex-col items-center justify-center rounded-2xl border border-dashed border-primary/20 bg-[#faf9f7] text-primary transition-colors hover:bg-[#f3f1ec] disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <ImagePlus className="h-7 w-7" />
            )}
            <span className="mt-1 px-1 text-center text-[10px] font-semibold leading-tight">
              {isUploading ? "Uploading" : "Add"}
            </span>
          </button>
        ) : null}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />

      {analyzing ? (
        <p className="text-xs font-medium text-primary">Suggesting details from your photo…</p>
      ) : (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary/60" />
          Upload up to {maxImages} photos. Empty name and description get a quick suggestion from the first image.
        </p>
      )}
    </div>
  );
}

const ensureDefaultFlag = (images: ProductImageAsset[]): ProductImageAsset[] => {
  if (images.length === 0) return images;
  return images.map((image, index) => ({
    ...image,
    isDefault: index === 0 ? true : Boolean(image.isDefault),
  }));
};
